import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ShieldCheck, Wind, CheckCircle2, XCircle, ChevronDown, Star, Clock, BookOpen, Droplets, Lock, X } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  text,
  subtext
}: {
  text: string;
  subtext?: string;
}) => <motion.a href="https://vitalizateahora.com/cart/52000694599911:1" target="_blank" rel="noopener noreferrer" whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className="group relative inline-flex flex-col items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-300 bg-gold-500 rounded-full shadow-[0_8px_20px_rgba(201,168,76,0.3)] hover:bg-gold-600 hover:shadow-[0_12px_25px_rgba(201,168,76,0.4)] overflow-hidden" data-id="element-1">
    <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-2" />
    <span className="flex items-center gap-2" data-id="element-3">{text}</span>
    {subtext && <span className="text-sm font-normal text-white/90 mt-1" data-id="element-4">{subtext}</span>}
  </motion.a>;
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  const faqs = [{
    q: '¿Necesito conocimientos previos?',
    a: 'Ninguno. El manual está diseñado para ser práctico, directo y fácil de entender para cualquier persona, sin importar su experiencia.'
  }, {
    q: '¿Tengo que comprar cosas raras o caras?',
    a: 'No, usamos elementos que probablemente ya tienes en tu cocina: sal, vinagre, limones y hierbas comunes como romero o canela.'
  }, {
    q: '¿Es seguro para mis mascotas y niños?',
    a: 'Sí, los métodos naturales son completamente seguros si se siguen las sencillas instrucciones de ventilación incluidas en el manual.'
  }, {
    q: '¿Cuánto tiempo toma hacer la limpieza?',
    a: 'La limpieza profunda inicial toma unos 20 minutos. Después, el mantenimiento diario o semanal te tomará solo 2 minutos.'
  }];
  const reviews = [{
    name: 'María Fernanda S.',
    text: 'Llevaba meses durmiendo mal y discutiendo con mi esposo por cualquier cosa. Hice la limpieza con sal y romero como dice el manual y esa misma noche dormimos de corrido. El ambiente se siente súper ligero.',
    rating: 5
  }, {
    name: 'Elena R.',
    text: "No creía mucho en estas cosas, pero estaba desesperada. El 'Circuito de Humo Blanco' cambió por completo la vibra de mi sala. Hasta mis plantas que estaban tristes empezaron a revivir.",
    rating: 5
  }, {
    name: 'Carmen T.',
    text: 'Lo mejor es que no tuve que gastar en inciensos caros ni contratar a nadie. Con lo que tenía en la alacena logré que mi casa volviera a sentirse como un refugio de paz.',
    rating: 5
  }];
  return <div className="min-h-screen bg-mint-50 overflow-hidden selection:bg-gold-500 selection:text-white" data-id="element-5">
      {/* Decorative Background Elements */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0" data-id="element-6">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-mint-200/30 blur-3xl" data-id="element-7" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-gold-400/10 blur-3xl" data-id="element-8" />
      </div>

      <div className="relative z-10" data-id="element-9">
        {/* HERO SECTION */}
        <section className="pt-20 pb-16 md:pt-32 md:pb-24 px-4 max-w-6xl mx-auto" data-id="element-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-11">
            <FadeIn className="flex-1 text-center lg:text-left" data-id="element-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mint-100 text-teal-800 text-sm font-medium mb-6" data-id="element-13">
                <Sparkles className="w-4 h-4 text-gold-500" data-id="element-14" />
                <span data-id="element-15">Método 100% Natural y Práctico</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-teal-900 leading-tight mb-6" data-id="element-16">
                Tu hogar ya no será el lugar que te agota:{' '}
                <span className="text-gold-600 italic" data-id="element-17">Recupera tu paz</span>
              </h1>
              <p className="text-lg md:text-xl text-teal-800/80 mb-8 leading-relaxed" data-id="element-18">
                Elimina la pesadez, las discusiones y el insomnio usando el{' '}
                <strong className="text-teal-900 font-semibold" data-id="element-19">
                  "Método de Alquimia de Cocina"
                </strong>{' '}
                en menos de 20 minutos (con lo que ya tienes en tu alacena).
              </p>
              <div className="mt-6 flex items-center justify-center lg:justify-start gap-4 text-sm text-teal-800/70" data-id="element-20">
                <div className="flex -space-x-2" data-id="element-21">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-mint-200 border-2 border-mint-50 flex items-center justify-center text-xs font-bold text-teal-800" data-id="element-22">
                      {String.fromCharCode(64 + i)}
                    </div>)}
                </div>
                <div className="flex items-center gap-1" data-id="element-23">
                  <div className="flex text-gold-500" data-id="element-24">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-25" />)}
                  </div>
                  <span className="font-medium" data-id="element-26">+500 hogares limpios</span>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="flex-1 w-full max-w-md lg:max-w-none" data-id="element-27">
              <div className="relative" data-id="element-28">
                <div className="absolute inset-0 bg-gradient-to-tr from-gold-400/20 to-mint-300/20 rounded-3xl transform rotate-3 scale-105 blur-lg" data-id="element-29" />
                <img src="https://cdn.magicpatterns.com/uploads/fobfpAFb7hbxZPCUCtQXze/heylloyd-image-1776868483901.png" alt="Manual de Limpieza Energética de Hogar en Tablet y Celular" className="relative z-10 w-full h-auto rounded-2xl shadow-soft object-cover" data-id="element-30" />

                {/* Floating badge */}
                <motion.div animate={{
                y: [0, -10, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl flex items-center gap-3 z-20" data-id="element-31">
                  <div className="w-12 h-12 bg-mint-100 rounded-full flex items-center justify-center text-teal-800" data-id="element-32">
                    <Clock className="w-6 h-6" data-id="element-33" />
                  </div>
                  <div data-id="element-34">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-35">
                      Resultados en
                    </p>
                    <p className="text-teal-900 font-bold" data-id="element-36">Solo 20 minutos</p>
                  </div>
                </motion.div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* THE HOOK & PROBLEM */}
        <section className="py-20 bg-white/60 backdrop-blur-sm relative" data-id="element-37">
          <div className="max-w-3xl mx-auto px-4" data-id="element-38">
            <FadeIn data-id="element-39">
              <h2 className="font-serif text-3xl md:text-4xl text-center text-teal-900 mb-10" data-id="element-40">
                Cruzas el umbral de tu puerta después de un día eterno...
              </h2>
              <div className="space-y-6 text-lg text-teal-800/80 leading-relaxed" data-id="element-41">
                <p data-id="element-42">
                  Deberías sentir alivio, pero en su lugar,{' '}
                  <strong className="text-teal-900" data-id="element-43">
                    sientes que el aire "pesa"
                  </strong>
                  . Las discusiones por tonterías estallan sin razón, las
                  plantas se secan y el cansancio parece pegado a las paredes.
                </p>
                <p data-id="element-44">
                  No es solo estrés acumulado. Es energía estancada que se ha
                  quedado atrapada en tu refugio. Tu casa no necesita solo orden
                  físico;{' '}
                  <span className="italic text-gold-600 font-medium" data-id="element-45">
                    necesita una liberación
                  </span>
                  .
                </p>

                <div className="bg-mint-50 p-8 rounded-2xl border border-mint-100 mt-10" data-id="element-46">
                  <h3 className="font-serif text-2xl text-teal-900 mb-4 flex items-center gap-2" data-id="element-47">
                    <Wind className="w-6 h-6 text-teal-700" data-id="element-48" />
                    El problema invisible
                  </h3>
                  <p className="mb-4" data-id="element-49">
                    La mayoría de las personas intentan solucionar esto
                    comprando ambientadores químicos o velas perfumadas. Pero el
                    perfume solo tapa el problema, no lo elimina.
                  </p>
                  <p data-id="element-50">
                    La densidad energética se acumula en las esquinas, bajo tu
                    cama y en los marcos de las puertas. Si te despiertas más
                    cansada de lo que te acostaste o sientes que la mala suerte
                    te persigue dentro de tus propias paredes,{' '}
                    <strong className="text-teal-900" data-id="element-51">
                      estás viviendo en un espacio saturado que consume tu
                      vitalidad segundo a segundo.
                    </strong>
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* WHY IT DIDN'T WORK & UNIQUE MECHANISM */}
        <section className="py-20 px-4 max-w-5xl mx-auto" data-id="element-52">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-53">
            <FadeIn data-id="element-54">
              <h3 className="text-sm font-bold tracking-widest text-gold-600 uppercase mb-3" data-id="element-55">
                Por qué lo que intentaste no funcionó
              </h3>
              <ul className="space-y-6" data-id="element-56">
                <li className="flex gap-4" data-id="element-57">
                  <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" data-id="element-58" />
                  <p className="text-teal-800/80" data-id="element-59">
                    <strong className="text-teal-900" data-id="element-60">
                      Inciensos de supermercado:
                    </strong>{' '}
                    Suelen ser aserrín con aroma sintético que solo ensucian el
                    aire sin limpiar la energía.
                  </p>
                </li>
                <li className="flex gap-4" data-id="element-61">
                  <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" data-id="element-62" />
                  <p className="text-teal-800/80" data-id="element-63">
                    <strong className="text-teal-900" data-id="element-64">
                      Expertos externos:
                    </strong>{' '}
                    Esperar a un "gurú" es caro y te quita el poder sobre tu
                    propio santuario.
                  </p>
                </li>
                <li className="flex gap-4" data-id="element-65">
                  <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" data-id="element-66" />
                  <p className="text-teal-800/80" data-id="element-67">
                    <strong className="text-teal-900" data-id="element-68">
                      Ignorar el problema:
                    </strong>{' '}
                    Intentar ignorar la sensación de "pesadez" solo hace que los
                    conflictos familiares crezcan hasta volverse inmanejables.
                  </p>
                </li>
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-69">
              <div className="bg-teal-900 text-white p-8 md:p-10 rounded-3xl shadow-xl relative overflow-hidden" data-id="element-70">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/20 rounded-full blur-2xl" data-id="element-71" />
                <h3 className="font-serif text-3xl mb-4 relative z-10" data-id="element-72">
                  La Solución: El Método{' '}
                  <span className="text-gold-400 italic" data-id="element-73">
                    "Alquimia de Cocina"
                  </span>
                </h3>
                <p className="text-mint-100/90 leading-relaxed mb-6 relative z-10" data-id="element-74">
                  Este manual te enseña a utilizar la frecuencia vibracional de
                  elementos puros como la sal, el romero y la canela.
                </p>
                <p className="text-mint-100/90 leading-relaxed relative z-10" data-id="element-75">
                  No son solo especias; son{' '}
                  <strong className="text-white" data-id="element-76">
                    herramientas de transmutación
                  </strong>{' '}
                  que desintegran la negatividad a nivel molecular, restaurando
                  la armonía de forma inmediata sin que tengas que comprar nada
                  nuevo.
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* WHAT YOU GET */}
        <section className="py-20 bg-cream border-y border-mint-100" data-id="element-77">
          <div className="max-w-5xl mx-auto px-4" data-id="element-78">
            <FadeIn className="text-center mb-16" data-id="element-79">
              <h2 className="font-serif text-3xl md:text-4xl text-teal-900 mb-4" data-id="element-80">
                Todo lo que incluye tu acceso hoy
              </h2>
              <p className="text-teal-800/70 max-w-2xl mx-auto" data-id="element-81">
                Herramientas prácticas, directas y sin relleno para transformar
                tu espacio.
              </p>
            </FadeIn>

            <div className="grid sm:grid-cols-2 gap-6" data-id="element-82">
              {[{
              icon: BookOpen,
              title: 'El Manual Completo (PDF)',
              desc: 'Tu hoja de ruta paso a paso. Teoría simple y práctica directa.'
            }, {
              icon: Wind,
              title: "Protocolo 'Circuito de Humo Blanco'",
              desc: 'El método exacto para limpiar cada rincón de forma segura y efectiva.'
            }, {
              icon: ShieldCheck,
              title: 'Sistema de Sellado de Puertas',
              desc: 'Cómo crear un filtro energético para que la envidia y el estrés no vuelvan a entrar.'
            }, {
              icon: Droplets,
              title: 'Receta: Spray de Armonía 2 Minutos',
              desc: 'La fórmula secreta para mantener tu casa vibrando alto todos los días sin esfuerzo.'
            }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-83">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-mint-50 flex gap-4 h-full hover:shadow-md transition-shadow" data-id="element-84">
                    <div className="w-12 h-12 bg-mint-100 rounded-xl flex items-center justify-center shrink-0 text-teal-700" data-id="element-85">
                      <item.icon className="w-6 h-6" data-id="element-86" />
                    </div>
                    <div data-id="element-87">
                      <h4 className="font-bold text-teal-900 mb-2" data-id="element-88">
                        {item.title}
                      </h4>
                      <p className="text-teal-800/70 text-sm leading-relaxed" data-id="element-89">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* WHO IS THIS FOR / NOT FOR */}
        <section className="py-20 px-4 max-w-6xl mx-auto" data-id="element-90">
          <div className="grid md:grid-cols-2 gap-8" data-id="element-91">
            <FadeIn data-id="element-92">
              <div className="bg-white p-8 rounded-3xl shadow-soft h-full border-t-4 border-teal-500" data-id="element-93">
                <h3 className="font-serif text-2xl text-teal-900 mb-6 flex items-center gap-2" data-id="element-94">
                  <CheckCircle2 className="w-6 h-6 text-teal-500" data-id="element-95" />
                  Esto ES para ti si:
                </h3>
                <ul className="space-y-4" data-id="element-96">
                  {['Mujeres que sienten que su casa "pesa" o que el ambiente está viciado.', 'Personas que sufren de insomnio o pesadillas recurrentes en su habitación.', 'Quienes acaban de pasar por una separación, enfermedad o racha de mala suerte.', 'Mamás que quieren proteger la paz de sus hijos y su matrimonio.'].map((text, i) => <li key={i} className="flex gap-3 text-teal-800/80" data-id="element-97">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-2.5 shrink-0" data-id="element-98" />
                      <span data-id="element-99">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-100">
              <div className="bg-white p-8 rounded-3xl shadow-soft h-full border-t-4 border-red-300 opacity-90" data-id="element-101">
                <h3 className="font-serif text-2xl text-teal-900 mb-6 flex items-center gap-2" data-id="element-102">
                  <XCircle className="w-6 h-6 text-red-400" data-id="element-103" />
                  Esto NO es para ti si:
                </h3>
                <ul className="space-y-4" data-id="element-104">
                  {['Personas que buscan rituales oscuros o complicados (esto es luz y simplicidad).', 'Quienes no están dispuestos a dedicar 10 minutos a cuidar su espacio.', 'Escépticos radicales que no creen en la influencia de la energía en el bienestar.'].map((text, i) => <li key={i} className="flex gap-3 text-teal-800/80" data-id="element-105">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-300 mt-2.5 shrink-0" data-id="element-106" />
                      <span data-id="element-107">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-20 bg-teal-900 text-white relative overflow-hidden" data-id="element-108">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-teal-800/50 via-transparent to-transparent pointer-events-none" data-id="element-109" />

          <div className="max-w-5xl mx-auto px-4 relative z-10" data-id="element-110">
            <FadeIn className="text-center mb-16" data-id="element-111">
              <h2 className="font-serif text-3xl md:text-4xl mb-4" data-id="element-112">
                ¿Cómo funciona?
              </h2>
              <p className="text-mint-100/70" data-id="element-113">
                Un proceso simple de 3 pasos para recuperar tu paz.
              </p>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-114">
              {/* Connecting line for desktop */}
              <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-teal-800 z-0" data-id="element-115" />

              {[{
              step: '1',
              title: 'Diagnóstico',
              desc: 'Identificas los puntos de fuga energética en tu hogar siguiendo las guías visuales del manual.'
            }, {
              step: '2',
              title: 'Aplicación',
              desc: 'Ejecutas el ritual de limpieza en 20 minutos con elementos básicos que ya tienes en tu cocina.'
            }, {
              step: '3',
              title: 'Protección',
              desc: 'Sellas tus puertas y ventanas para que la paz sea permanente y la densidad no regrese.'
            }].map((item, i) => <FadeIn key={i} delay={i * 0.2} className="relative z-10 text-center" data-id="element-116">
                  <div className="w-24 h-24 mx-auto bg-teal-800 rounded-full flex items-center justify-center border-4 border-teal-900 shadow-xl mb-6 relative" data-id="element-117">
                    <span className="font-serif text-4xl text-gold-400" data-id="element-118">
                      {item.step}
                    </span>
                  </div>
                  <h4 className="text-xl font-bold mb-3 text-mint-50" data-id="element-119">
                    {item.title}
                  </h4>
                  <p className="text-mint-100/70 leading-relaxed" data-id="element-120">
                    {item.desc}
                  </p>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* THE FINAL TOUCH / VIDEO SECTION */}
        <section className="py-20 bg-mint-50 relative overflow-hidden" data-id="element-121">
          <div className="max-w-5xl mx-auto px-4" data-id="element-122">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-soft border border-mint-100 flex flex-col md:flex-row items-center gap-12" data-id="element-123">
              <FadeIn className="flex-1 w-full max-w-sm mx-auto md:mx-0" data-id="element-124">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[9/16] bg-mint-100" data-id="element-125">
                  <video src="https://cdn.magicpatterns.com/uploads/kWCgKpsANAsXfBt6tFXts2/Cinnamon_spray_for_202604221205.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-126" />
                  {/* Decorative overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-teal-900/40 to-transparent pointer-events-none" data-id="element-127" />
                </div>
              </FadeIn>

              <FadeIn delay={0.2} className="flex-1 text-center md:text-left" data-id="element-128">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-50 text-gold-600 text-sm font-medium mb-6" data-id="element-129">
                  <Sparkles className="w-4 h-4" data-id="element-130" />
                  <span data-id="element-131">El Toque Final</span>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-teal-900 mb-6 leading-tight" data-id="element-132">
                  "Después de limpiar,{' '}
                  <span className="text-gold-500 italic" data-id="element-133">hay que elevar</span>."
                </h2>
                <p className="text-lg text-teal-800/80 mb-6 leading-relaxed" data-id="element-134">
                  Tu casa no solo debe estar vacía de lo malo,{' '}
                  <strong className="text-teal-900" data-id="element-135">
                    debe estar llena de lo bueno.
                  </strong>
                </p>
                <p className="text-teal-800/70 mb-8 leading-relaxed" data-id="element-136">
                  Con la receta del{' '}
                  <strong data-id="element-137">Spray de Armonía de 2 Minutos</strong> (incluida en tu
                  manual), podrás sellar el trabajo energético diario. Un rocío
                  rápido de canela y elementos naturales para dejar un ambiente
                  vibrante, cálido y protector.
                </p>
                <ul className="space-y-3" data-id="element-138">
                  {['Aroma natural y reconfortante', 'Eleva la frecuencia en segundos', 'Sella la protección de tu hogar'].map((text, i) => <li key={i} className="flex items-center justify-center md:justify-start gap-3 text-teal-800/80" data-id="element-139">
                      <CheckCircle2 className="w-5 h-5 text-mint-400" data-id="element-140" />
                      <span data-id="element-141">{text}</span>
                    </li>)}
                </ul>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* PROOF & REVIEWS */}
        <section className="py-20 px-4 max-w-5xl mx-auto" data-id="element-142">
          <FadeIn className="text-center mb-16" data-id="element-143">
            <h2 className="font-serif text-3xl md:text-4xl text-teal-900 mb-6" data-id="element-144">
              Lo que cambia en 7 días
            </h2>
            <p className="text-lg text-teal-800/80 max-w-3xl mx-auto leading-relaxed" data-id="element-145">
              Notarás que el aire se siente más ligero, las discusiones
              disminuyen y, por primera vez en mucho tiempo, entrar a tu
              habitación te dará una sensación real de descanso profundo y
              seguridad.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-146">
            {reviews.map((review, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-147">
                <div className="bg-white p-8 rounded-2xl shadow-soft h-full flex flex-col" data-id="element-148">
                  <div className="flex text-gold-500 mb-4" data-id="element-149">
                    {[...Array(review.rating)].map((_, j) => <Star key={j} className="w-5 h-5 fill-current" data-id="element-150" />)}
                  </div>
                  <p className="text-teal-800/80 italic mb-6 grow" data-id="element-151">
                    "{review.text}"
                  </p>
                  <p className="font-bold text-teal-900" data-id="element-152">— {review.name}</p>
                </div>
              </FadeIn>)}
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-mint-100/50" data-id="element-153">
          <div className="max-w-3xl mx-auto px-4" data-id="element-154">
            <FadeIn className="text-center mb-12" data-id="element-155">
              <h2 className="font-serif text-3xl md:text-4xl text-teal-900" data-id="element-156">
                Preguntas Frecuentes
              </h2>
            </FadeIn>

            <div className="space-y-4" data-id="element-157">
              {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-158">
                  <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-mint-100" data-id="element-159">
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" data-id="element-160">
                      <span className="font-semibold text-teal-900 pr-4" data-id="element-161">
                        {faq.q}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-teal-600 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-162" />
                    </button>
                    <AnimatePresence data-id="element-163">
                      {openFaq === i && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-164">
                          <div className="px-6 pb-5 text-teal-800/70 border-t border-mint-50 pt-4" data-id="element-165">
                            {faq.a}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* OFFER & CTA */}
        <section id="oferta" className="py-24 px-4 relative" data-id="element-166">
          <div className="max-w-4xl mx-auto" data-id="element-167">
            <FadeIn data-id="element-168">
              <div className="bg-teal-900 rounded-[2.5rem] p-8 md:p-16 text-center relative overflow-hidden shadow-2xl" data-id="element-169">
                {/* Decorative background for the offer card */}
                <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-gold-500/20 blur-3xl pointer-events-none" data-id="element-170" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-mint-400/20 blur-3xl pointer-events-none" data-id="element-171" />

                <div className="relative z-10" data-id="element-172">
                  <h2 className="font-serif text-3xl md:text-5xl text-white mb-6 leading-tight" data-id="element-173">
                    Recupera la armonía de tu hogar{' '}
                    <span className="text-gold-400 italic" data-id="element-174">hoy mismo</span>.
                  </h2>
                  <p className="text-lg md:text-xl text-mint-100/90 mb-10 max-w-2xl mx-auto" data-id="element-175">
                    Deja de vivir en un ambiente que te drena y empieza a
                    disfrutar de tu propio santuario.
                  </p>

                  <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 max-w-md mx-auto mb-10 border border-white/20" data-id="element-176">
                    <p className="text-mint-100/80 mb-2 line-through text-lg" data-id="element-177">
                      Antes pagabas: $22.00
                    </p>
                    <div className="flex items-center justify-center gap-4 mb-2" data-id="element-178">
                      <span className="text-white text-2xl font-medium" data-id="element-179">
                        Hoy solo
                      </span>
                      <span className="text-gold-400 text-6xl font-bold tracking-tighter" data-id="element-180">
                        $9
                      </span>
                    </div>
                    <p className="text-sm text-mint-100/70 mt-4 flex items-center justify-center gap-2" data-id="element-181">
                      <Lock className="w-4 h-4" data-id="element-182" /> Pago 100% Seguro
                    </p>
                  </div>

                  <CTAButton text="Descargar Mi Manual Ahora" data-id="element-183" />

                  <p className="text-mint-100/60 text-sm mt-6" data-id="element-184">
                    Recibe el acceso a tu PDF al instante en tu correo.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="py-8 text-center text-teal-800/50 text-sm border-t border-mint-200" data-id="element-185">
          <div className="max-w-4xl mx-auto px-4 flex flex-col items-center gap-4" data-id="element-186">
            <p data-id="element-187">
              © {new Date().getFullYear()} Manual de Limpieza Energética. Todos
              los derechos reservados.
            </p>
            <div className="flex gap-6" data-id="element-188">
              <button onClick={() => setModalContent('privacy')} className="hover:text-teal-800 transition-colors underline underline-offset-4" data-id="element-189">
                Política de Privacidad
              </button>
              <button onClick={() => setModalContent('terms')} className="hover:text-teal-800 transition-colors underline underline-offset-4" data-id="element-190">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </footer>
      </div>

      {/* MODALS */}
      <AnimatePresence data-id="element-191">
        {modalContent && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-192">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-teal-900/40 backdrop-blur-sm" onClick={() => setModalContent(null)} data-id="element-193" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-3xl p-8 max-w-lg w-full relative z-10 shadow-2xl max-h-[80vh] overflow-y-auto" data-id="element-194">
              <button onClick={() => setModalContent(null)} className="absolute top-6 right-6 text-teal-800/50 hover:text-teal-900 transition-colors" data-id="element-195">
                <X className="w-6 h-6" data-id="element-196" />
              </button>
              <h3 className="font-serif text-2xl text-teal-900 mb-4" data-id="element-197">
                {modalContent === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
              </h3>
              <div className="text-teal-800/80 space-y-4 text-sm leading-relaxed" data-id="element-198">
                {modalContent === 'privacy' ? <>
                    <p data-id="element-199">
                      Su privacidad es importante para nosotros. Esta política
                      explica cómo recopilamos, usamos y protegemos su
                      información personal al interactuar con nuestro sitio web
                      y adquirir nuestros productos digitales.
                    </p>
                    <p data-id="element-200">
                      <strong data-id="element-201">Recopilación de Datos:</strong> Solo recopilamos
                      la información necesaria para procesar su compra y
                      entregar el producto digital (como su nombre y correo
                      electrónico). No almacenamos información financiera
                      directamente.
                    </p>
                    <p data-id="element-202">
                      <strong data-id="element-203">Uso de la Información:</strong> Utilizamos sus
                      datos exclusivamente para enviarle el producto adquirido,
                      proporcionarle actualizaciones relevantes y mejorar su
                      experiencia en nuestro sitio.
                    </p>
                    <p data-id="element-204">
                      <strong data-id="element-205">Protección:</strong> Implementamos medidas de
                      seguridad estándar para proteger sus datos contra acceso
                      no autorizado. No vendemos, alquilamos ni compartimos su
                      información personal con terceros bajo ninguna
                      circunstancia.
                    </p>
                  </> : <>
                    <p data-id="element-206">
                      Al acceder y utilizar este sitio web, así como al adquirir
                      el "Manual de Limpieza Energética de Hogar", usted acepta
                      los siguientes términos y condiciones.
                    </p>
                    <p data-id="element-207">
                      <strong data-id="element-208">Naturaleza del Producto:</strong> El manual es un
                      producto digital con fines puramente informativos, de
                      bienestar personal y entretenimiento. No constituye ni
                      sustituye el consejo médico, psicológico, psiquiátrico o
                      profesional de ninguna índole.
                    </p>
                    <p data-id="element-209">
                      <strong data-id="element-210">Resultados:</strong> Los resultados derivados de
                      la aplicación de los métodos descritos en el manual son
                      subjetivos y pueden variar significativamente de una
                      persona a otra. No garantizamos resultados específicos.
                    </p>
                    <p data-id="element-211">
                      <strong data-id="element-212">Propiedad Intelectual:</strong> Todo el contenido,
                      texto y diseño de este manual están protegidos por
                      derechos de autor. Queda estrictamente prohibida su
                      reproducción, distribución o reventa sin autorización
                      previa.
                    </p>
                  </>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}